import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import type { PublicPlans } from "@/hooks/queries/usePublicPlans";
import { SectionHeading, SECTION_CLASS } from "./SectionHeading";

const pct = (n: number) => `${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
const code = "font-fragment text-[13px]";

function buildFaqs({ plans }: PublicPlans): { q: string; a: ReactNode }[] {
  const rates = plans.map((p) => p.features.byokFeePercent);
  const range = rates.length ? `${pct(Math.min(...rates))}–${pct(Math.max(...rates))}` : "a small percentage";
  const clientLimits = plans.filter((p) => p.features.maxClients > 0).map((p) => `${p.displayName}: up to ${p.features.maxClients}`);

  return [
    {
      q: "Why not go direct to my cloud provider?",
      a: (
        <>
          You still can, and with GenAICore you keep doing so: connect your AWS Bedrock, Azure OpenAI or Google Gemini keys and the
          provider keeps billing you at your negotiated rates. GenAICore adds what sits above the model: hosted knowledge bases,
          agents and workflows that work on any provider, one OpenAI- and Anthropic-compatible API, failover across providers,
          and client accounts with prepaid wallets and your markup.
        </>
      ),
    },
    {
      q: "Do I pay twice when I use my own provider keys?",
      a: (
        <>
          No. The provider bills you for the tokens. GenAICore charges only its platform fee ({range} of the model's list price,
          depending on your plan) from your wallet.
        </>
      ),
    },
    {
      q: "How do agencies earn?",
      a: (
        <>
          On an Agency plan you create client accounts{clientLimits.length ? ` (${clientLimits.join(", ")})` : ""}. Each client has its
          own API key, wallet and usage, and can call your knowledge bases, agents and workflows. You set a markup; it is added to
          every client request and credited to your agency wallet. Earnings are wallet credit that you can spend on GenAICore; they
          aren't paid out to a bank account.
        </>
      ),
    },
    {
      q: "What happens when a wallet reaches $0?",
      a: (
        <>
          Model calls are refused with HTTP 402 until credit is added, so nobody gets a surprise bill. A request that is already
          running finishes, so a balance can dip slightly below zero. A low-balance alert warns you beforehand. If a plan renewal
          finds too little credit, the account moves to the free Developer plan.
        </>
      ),
    },
    {
      q: "Which SDKs work?",
      a: (
        <>
          The official OpenAI SDKs (Chat Completions, Responses, embeddings) with <code className={code}>base_url="https://api.genaicore.com/v1"</code>,
          and the official Anthropic SDKs (Messages) with <code className={code}>base_url="https://api.genaicore.com"</code>, on every
          provider. Any HTTP client works too. See the{" "}
          <Link to="/docs/api/compatibility" className="underline underline-offset-2">API reference</Link>.
        </>
      ),
    },
  ];
}

export function LandingFaq({ data }: { data: PublicPlans }) {
  const faqs = buildFaqs(data);
  return (
    <section id="faq" className={SECTION_CLASS}>
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
        <SectionHeading eyebrow="FAQ" title="Questions" accent="we hear from agencies." />
        <div className="rounded-card bg-card px-6 py-2">
          <Accordion type="single" collapsible>
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className={i === faqs.length - 1 ? "border-b-0" : "border-border/60"}>
                <AccordionTrigger className="text-left text-[15px] font-medium hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[15px] text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
